import { Default, DraggablePoint, LabelText, LabelTextConfig, LimitUtils, LinePointToPoint2, Save, SaveChild, SelectedApexMech, ToolEvents, ToolToSave, ToolType, ViewType } from '@ortho-next/nextray-core';
import { Group, Vector3 } from 'three';
import { Config } from '../../Config/Config';
import { ReferencePoint } from '../ReferencePoint';

export abstract class Osteotomy extends Group<ToolEvents> implements ToolToSave {
	public toolType = ToolType.osteotomy;
	@Save('position') public A: DraggablePoint;
	@Save('position') public B: DraggablePoint;
	@Save('position') public C: DraggablePoint;
	public ABLine: LinePointToPoint2;
	@SaveChild() public label: LabelText;
	@Save() public selectedApex!: SelectedApexMech;
	public referencePoint: ReferencePoint;
	@Save() protected _axisPoint1!: Vector3;
	@Save() protected _axisPoint2!: Vector3;
	@Save() protected _axisPerp!: Vector3;
	@Save() protected _axisDir!: Vector3;
	@Save() protected _axisValidPoint!: Vector3;
	protected _initialLength = 30;
	protected _perpTranslToSync: Vector3 | undefined;
	protected _enableLimits: boolean;
	protected _errorLength!: boolean;

	public abstract get distanceToReferencePoint(): number;
	public abstract set distanceToReferencePoint(value: number);
	public abstract setPositionByApex(selectedApex: SelectedApexMech, distanceToReferenceePoint?: number): void;
	protected abstract updatePerpTranslation(): void;

	public get length(): number {
		return this.A.position.distanceTo(this.B.position);
	}

	constructor(viewType: ViewType, referencePoint: ReferencePoint, enableLimits: boolean) {
		super();
		this.name = 'Osteotomy';
		this.referencePoint = referencePoint;
		this._enableLimits = enableLimits;

		this.add(
			this.A = new DraggablePoint('A', viewType, Config.osteotomy_colorHex, new Vector3(this._initialLength / 2, 0, 0)),
			this.B = new DraggablePoint('B', viewType, Config.osteotomy_colorHex, new Vector3(this._initialLength / -2, 0, 0)),
			this.C = new DraggablePoint('C', viewType, Config.osteotomy_colorHex, this.A.position.clone().lerp(this.B.position, 0.5)),
			this.ABLine = new LinePointToPoint2('ABLine', viewType, Config.osteotomy_colorHex, this.A, this.B),
			this.label = new LabelText({
				name: 'label',
				color: LabelTextConfig.color, //todo refactor
				viewType,
				objToAnchor: this.C,
				anchor: new Vector3(0, 5, 0),
				labelParams: { text: 'Osteotomy', backgroundColor: Config.osteotomy_colorCssAlpha, fontColor: Config.osteotomy_fontColor, borderColor: Config.osteotomy_colorCss }
			})
		);

		this.bindEvents();
	}

	protected bindEvents(): void {
		Default.bindOnEnabledChange_NoRaycastAndHiddenLabel(this);

		this.addEventListener('historyChanged', () => {
			this.dispatchEvent({ type: 'updated' });
		});

		this.A.bindEvent('onDragMove', (args) => {
			this._enableLimits && LimitUtils.applyMultiLimits(args.position, this.A.position, [
				{ a: this._axisPoint1, b: this._axisPoint1.clone().add(this._axisPerp), v: this._axisValidPoint },
				{ a: this._axisPoint2, b: this._axisPoint2.clone().add(this._axisPerp), v: this._axisValidPoint }
			]);
			this.C.position.copy(args.position.clone().lerp(this.B.position, 0.5));
		});

		this.B.bindEvent('onDragMove', (args) => {
			this._enableLimits && LimitUtils.applyMultiLimits(args.position, this.A.position, [
				{ a: this._axisPoint1, b: this._axisPoint1.clone().add(this._axisPerp), v: this._axisValidPoint },
				{ a: this._axisPoint2, b: this._axisPoint2.clone().add(this._axisPerp), v: this._axisValidPoint }
			]);
			this.C.position.copy(args.position.clone().lerp(this.A.position, 0.5));
		});

		this.C.bindEvent('onDragMove', (args) => {
			this._enableLimits && LimitUtils.applyLimitAndProjectVectorCenterLinePoint(this._axisPoint1, this._axisPoint1.clone().add(this._axisPerp), this.C.position, args.position, this.A.position, this.B.position, this._axisValidPoint);
			this._enableLimits && LimitUtils.applyLimitAndProjectVectorCenterLinePoint(this._axisPoint2, this._axisPoint2.clone().add(this._axisPerp), this.C.position, args.position, this.A.position, this.B.position, this._axisValidPoint);
			this.translateAB(args.position);
		});
	}


	protected updateCenter(newCenter: Vector3): void {
		this.translateAB(newCenter);
		this.C.position.copy(newCenter);
	}

	protected translateAB(newCenter: Vector3): void {
		const pan = this.C.position.clone().sub(newCenter);
		this.A.position.sub(pan);
		this.B.position.sub(pan);
	}

	protected placeOsteotomyByApex(origin: Vector3, direction: Vector3): void {
		const dir = direction.clone().setLength(this._initialLength / 2);
		this.C.position.copy(origin);
		this.A.position.copy(origin).add(dir);
		this.B.position.copy(origin).sub(dir);
	}
}
